import React from "react";
import * as Sentry from "@sentry/react";

// Error Boundary component that catches React errors and sends them to Sentry
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to Sentry with enhanced context
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", true);
      scope.setTag("component", this.props.componentName || "unknown");
      scope.setContext("errorInfo", errorInfo);
      scope.setContext("componentStack", errorInfo.componentStack);
      scope.setContext("errorBoundary", {
        componentName: this.props.componentName || "unknown",
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
      Sentry.captureException(error);
    });

    // Log to console for development
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#f8f9fa"
        }}>
          <div style={{
            maxWidth: "500px",
            padding: "30px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
          }}>
            <h2 style={{ color: "#dc3545", marginBottom: "20px" }}>
              Something went wrong
            </h2>
            <p style={{ color: "#6c757d", marginBottom: "20px" }}>
              We're sorry, but something unexpected happened. Our team has been notified and is working to fix this issue.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              style={{
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px"
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component that wraps components with Sentry error boundary
export const withSentryErrorBoundary = (Component, fallback = null) => {
  // Ensure fallback is a React element or render function
  const fallbackElement = fallback || <ErrorBoundary />;

  return Sentry.withErrorBoundary(Component, {
    fallback: fallbackElement,
    beforeCapture: (scope, error, errorInfo) => {
      scope.setTag("errorBoundary", "withSentryErrorBoundary");
      scope.setContext("errorInfo", errorInfo);
    },
  });
};

export default ErrorBoundary;
